import React, {useState, useContext, useEffect} from 'react';
import {Upload, File, Trash2, X} from 'lucide-react';
import {FamilyContext} from '../../Context/FamilyHeadContext';
import {useTranslation} from "react-i18next";
import {API_URL, doApiMethod} from "../../services/apiService";
import {getCleanFileName} from "../../utils/dateUtils";

const EssayManagement = ({isOpen, onClose, onSave, localFiles, setLocalFiles}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const {currentFamilyHead, isMainSite} = useContext(FamilyContext);
    const {t} = useTranslation();

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        setIsSubmitting(true);
        setError(null);

        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);

                const response = await doApiMethod(
                    `${API_URL}/Essays/upload/${currentFamilyHead._id}/${currentFamilyHead.isMain ? 'bazach' : 'humenir'}`,
                    'POST',
                    formData,
                    'multipart/form-data'
                );
                if (response.files) {
                    setLocalFiles(response.files);
                    onSave(response.files);
                }
            }
        } catch (error) {
            console.error('שגיאת העלאה:', error);
            setError(t('שגיאת העלאה') || 'שגיאה בהעלאת הקובץ');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (filePath) => {
        setIsSubmitting(true);
        setError(null);

        try {
            const response = await doApiMethod(
                `${API_URL}/Essays/delete-file/${isMainSite}`,
                'DELETE',
                {
                    id: currentFamilyHead._id,
                    filePath
                }
            );
            if (response.files) {
                // עדכון ה-state המקומי לפני העדכון בקונטקסט
                setLocalFiles(response.files);
                onSave(response.files);
            }
        } catch (error) {
            console.error('שגיאת מחיקה:', error);
            setError(t('שגיאת מחיקה') || 'שגיאה במחיקת הקובץ');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="fixed inset-y-0 right-0 max-w-full flex">
                <div className="w-screen max-w-xl">
                    <div className="h-full flex flex-col bg-white">
                        <div className="p-4 border-b border-[#937c5d]">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-[#937c5d]">{t('ניהול חיבורים')}</h2>
                                <button
                                    onClick={onClose}
                                    className="text-[#937c5d] hover:text-[#7a6446]"
                                    disabled={isSubmitting}
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {error && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                                    {error}
                                </div>
                            )}

                            <div className="mb-6">
                                <label className={`flex flex-col items-center p-6 border-2 border-dashed rounded-lg 
                                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#937c5d]'}`}>
                                    <Upload className="w-8 h-8 text-[#937c5d] mb-2"/>
                                    <span className="text-sm text-[#937c5d]">
                                        {isSubmitting ? t('מעלה...') : t('לחץ או גרור קבצים לכאן')}
                                    </span>
                                    <input
                                        type="file"
                                        multiple
                                        className="hidden"
                                        onChange={handleFileUpload}
                                        accept=".pdf,.doc,.docx"
                                        disabled={isSubmitting}
                                    />
                                </label>
                            </div>

                            <div className="space-y-4">
                                {localFiles.map((filePath, index) => (
                                    <div key={index} className="flex items-center gap-4 p-4 bg-[#f8f5f2] rounded-lg">
                                        <File className="w-6 h-6 text-[#937c5d]"/>
                                        <span className="flex-1 break-all text-right">
            {getCleanFileName(filePath)}
        </span>
                                        <button
                                            onClick={() => handleDelete(filePath)}
                                            className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                            disabled={isSubmitting}
                                        >
                                            <Trash2 className="w-5 h-5"/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 border-t border-[#937c5d]">
                            <button
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center gap-2 p-2 bg-[#937c5d] text-white rounded-lg hover:bg-[#7a6446] disabled:opacity-50"
                            >
                                <span>{t('סגור')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EssayManagement;